import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../utils/api';
import { useAlert } from '../components/AlertModal';
import '../styles/ReturnModal.css';

const ReturnModal = ({ onClose, register, setShowReturnModal, storeId, setBarcodeFocusEnabled }) => {
  const [receiptId, setReceiptId] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [reason, setReason] = useState('');
  const [totalRefund, setTotalRefund] = useState(0);
  const { showAlert } = useAlert();
  const inputRef = useRef(null);
  
  useEffect(() => {
    // モーダルが開いたときにフォーカスをセット
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  useEffect(() => {
    // details が更新された場合にのみ処理を行う
    if (details.length > 0) {
    }
  }, [details]); // details が変更されるたびに実行されます

  // 返金金額の計算
  useEffect(() => {
    if (!receipt) return;

    const refundAmount = selectedItems.reduce((sum, item) => {
      const itemTotal = item.unit_price * item.return_quantity;
      const shouldRefundTax =
        receipt.sale_type === "normal" ||
        (
          receipt.sale_type === "tax_exempt" &&
          receipt.tax_free_mode === "refund"
        );

      const taxAmount = shouldRefundTax
        ? Math.floor(itemTotal * 0.1)
        : 0;
      return sum + itemTotal + taxAmount;
    }, 0);
    setTotalRefund(refundAmount);
  }, [selectedItems, receipt]); 

  const fetchReceiptDetails = async () => {
    try {
      setLoading(true);  // ローディング開始
      setTotalRefund(0); //リセット
      setSelectedItems([]);  //リセット

      const receiptData = await apiRequest(`inventory/sales_receipts/${receiptId}/`, 'GET');

      // ★ 返品済みチェック
      if (receiptData.is_returned) {
        showAlert('このレシートは既に返品済みです。');
        return;
      }

      setReceipt(receiptData);

      const detailData = await apiRequest(`inventory/sales_receipts/${receiptId}/details/`, 'GET');
      if (detailData && detailData.details && detailData.details.length > 0) {
        setDetails(detailData.details);
      } else {
        showAlert('詳細データが見つかりませんでした');
      }
      } catch (error) {
      showAlert('販売履歴が見つかりません');
      }finally {
      setLoading(false);  // ローディング終了
      }   
    };

  const toggleItem = (item) => {
    const exists = selectedItems.find(i => i.id === item.id);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, { ...item, return_quantity: item.quantity }]);
    }
  };

  const handleReturn = async () => {
    if (!selectedItems.length) {
      showAlert('返品対象を選んでください');
      return;
    }

    try {
      const payload = {
        receipt_id: receiptId,
        register_id: register,
        store_id: storeId,
        refund_method: refundMethod,
        reason,
        selected_items: selectedItems.map(item => ({
          id: item.id,
          inventory_id: item.inventory ? item.inventory.id : null,
          quantity: item.quantity,
          return_quantity: item.return_quantity,
          unit_price: item.unit_price,
          subtotal: item.subtotal,
        })),
      };

      const result = await apiRequest('inventory/register/return/', 'POST', payload);

      showAlert(`返品処理が完了しました（返金額: ¥${Number(result.refund_total).toLocaleString()}）`);
      onClose();
    } catch (error) {
      console.error(error);
      const message = error?.details?.error || '返品処理に失敗しました';
      showAlert(message);
    }
  };

  // モーダルを閉じる
  const onCloseReturnModal = (event) => {
    // モーダル本体以外をクリックした場合に閉じる
    if (event.target === event.currentTarget) {
      event.stopPropagation(); // イベントの伝播を停止
      setShowReturnModal(false); // モーダルを閉じる
      setBarcodeFocusEnabled(true); // フォーカスを自動に戻す
    }
  };
    
  
  return (
    <div className="returnModal" onClick={onCloseReturnModal}>
      <div className="returnModal__content">
        <h2>返品処理</h2>
        <input
          ref={inputRef}
          placeholder="レシート番号を入力"
          value={receiptId}
          onChange={(e) => setReceiptId(e.target.value)}
          className="returnModal__content_inputnumber"
        />
        <button onClick={fetchReceiptDetails} className="returnModal__content_search">伝票検索</button>
        {receipt && (
        <div className="returnModal__receiptInfo">
            伝票番号：{receipt.id}　
            購入日：{new Date(receipt.date).toLocaleString()}　
            場所：{receipt.register.name}<br></br>
            支払方法：{receipt.payment_method}　
            販売種別：
            {receipt.sale_type === "tax_exempt"
              ? receipt.tax_free_mode === "refund"
                ? "免税（リファンド方式）"
                : "免税（従来方式）"
              : "通常"}<br></br>
            商品合計：¥{Number(receipt.total_amount).toLocaleString()}　
            税金：¥{Number(receipt.tax_amount).toLocaleString()}　
            預かり金額：¥{Number(receipt.cash_received_amount).toLocaleString()}　
            キャッシュレス：¥{Number(receipt.cashless_payment_amount).toLocaleString()}　
            お釣り：¥{(Number(receipt.cash_received_amount) + Number(receipt.cashless_payment_amount) - Number(receipt.tax_amount) - Number(receipt.total_amount)).toLocaleString()}
        </div>
        )}
        <div className="returnModal__list">
          {loading ? (
            <p>データ読み込み中...</p>
          ) : (
            details.map(item => (
              <div key={item.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedItems.some(i => i.id === item.id)}
                    onChange={() => toggleItem(item)}
                  />
                  {item.product_name}（{item.quantity}個 / 単価{item.unit_price}円）
                </label>
              </div>
            ))
          )}
        </div>
        <div className="returnModal__refundAmount">
          <h3>返金金額: ¥{totalRefund.toLocaleString()}</h3>
        </div>
        <div className="returnModal__reazon">返品理由</div>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="returnModal__content_select">
          <option value="">未選択</option>
          <option value="商品の不具合による返品">商品の不具合による返品</option>
          <option value="お客様の都合による返品">お客様の都合による返品</option>
        </select>

        <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} className="returnModal__content_select">
          <option value="cash">現金</option>
          <option value="card">カード</option>
        </select>

        <div className="returnModal__actions">
          <button onClick={handleReturn}>返品確定</button>
          <button onClick={onClose}>キャンセル</button>
        </div>
      </div>
    </div>
  );
};

export default ReturnModal;
